(() => {
if (window.__weberCommerceLoaded) return;
window.__weberCommerceLoaded = true;

document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const root = window.Shopify?.routes?.root || '/';
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const motionDuration = () => prefersReducedMotion.matches ? 0 : 260;
const debounce = (callback, wait = 250) => {
  let timeout;
  return (...args) => {
    window.clearTimeout(timeout);
    timeout = window.setTimeout(() => callback(...args), wait);
  };
};

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button) return;
      const step = button.dataset.action === 'increase' ? 1 : -1;
      const minimum = Number(this.input.min || 0);
      const maximum = Number(this.input.max || Infinity);
      this.input.value = Math.min(maximum, Math.max(minimum, Number(this.input.value) + step));
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}

class VariantSelects extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById(this.dataset.form);
    this.variants = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
    this.addEventListener('change', () => this.update());
  }

  update() {
    const selectedOptions = [...this.querySelectorAll('fieldset')].map((fieldset) => fieldset.querySelector('input:checked')?.value);
    const variant = this.variants.find((item) => item.options.every((option, index) => option === selectedOptions[index]));
    const idInput = this.form?.querySelector('[name="id"]');
    const button = this.form?.querySelector('[name="add"]');
    const price = document.querySelector('[data-product-price]');
    const availability = document.querySelector('[data-product-availability]');
    if (!variant) {
      if (idInput) idInput.value = '';
      if (button) {
        button.disabled = true;
        button.querySelector('span').textContent = this.dataset.unavailable;
      }
      if (availability) availability.textContent = this.dataset.unavailable;
      const payment = this.form?.querySelector('.shopify-payment-button');
      if (payment) payment.hidden = true;
      return;
    }
    if (idInput) {
      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (button) {
      button.disabled = !variant.available;
      button.querySelector('span').textContent = variant.available
        ? this.dataset.add
        : variant.price_pending ? this.dataset.pricePending : this.dataset.soldOut;
    }
    const payment = this.form?.querySelector('.shopify-payment-button');
    if (payment) payment.hidden = !variant.available;
    if (price) {
      price.innerHTML = variant.price_html;
    }
    if (availability) {
      availability.textContent = variant.available
        ? this.dataset.available
        : variant.price_pending ? this.dataset.pricePending : this.dataset.soldOut;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
    if (variant.featured_media_id) {
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      document.getElementById(`Media-${variant.featured_media_id}`)?.scrollIntoView({ behavior, block: 'nearest', inline: 'start' });
    }
  }
}

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.error = this.querySelector('[data-product-form-error]');
    this.form.addEventListener('submit', (event) => this.submit(event));
  }

  async submit(event) {
    const button = this.form.querySelector('[name="add"]');
    if (this.hasAttribute('data-unavailable') || button?.disabled) {
      event.preventDefault();
      return;
    }
    if (!window.fetch || !document.querySelector('cart-drawer')) return;
    event.preventDefault();
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
    this.error.hidden = true;
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(this.form)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message);
      await document.querySelector('cart-drawer').refresh(true);
    } catch (error) {
      this.error.textContent = error.message || this.dataset.error;
      this.error.hidden = false;
      this.error.focus();
    } finally {
      button.removeAttribute('aria-busy');
      button.classList.remove('is-loading');
      const picker = this.querySelector('variant-selects');
      if (picker) picker.update();
      else button.disabled = this.hasAttribute('data-unavailable');
    }
  }
}

class CartDrawer extends HTMLElement {
  connectedCallback() {
    if (document.querySelector('cart-drawer') !== this) return;
    this.dialog = this.querySelector('dialog');
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-close]')) this.close();
      if (event.target === this.dialog) this.close();
    });
    this.addEventListener('change', (event) => {
      const input = event.target.closest('[data-cart-line]');
      if (input) this.change(input.dataset.cartLine, input.value);
    });
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-cart-open], a[href$="/cart"]');
      if (!trigger || trigger.closest('cart-drawer')) return;
      event.preventDefault();
      this.open(trigger);
    });
  }

  open(trigger) {
    this.trigger = trigger;
    this.dialog.classList.remove('is-closing');
    if (!this.dialog.open) this.dialog.showModal();
    document.body.classList.add('drawer-open');
    window.requestAnimationFrame(() => this.dialog.querySelector('[data-cart-close]')?.focus());
  }

  close() {
    if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;
    this.dialog.classList.add('is-closing');
    document.body.classList.remove('drawer-open');
    window.setTimeout(() => {
      this.dialog.close();
      this.dialog.classList.remove('is-closing');
      this.trigger?.focus();
    }, motionDuration());
  }

  async refresh(open = false) {
    const shouldOpen = open || this.dialog.open;
    const trigger = this.trigger || document.activeElement;
    const response = await fetch(`${root}cart?section_id=cart-drawer`);
    if (!response.ok) throw new Error(this.dataset.error);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const replacement = html.querySelector('cart-drawer');
    if (!replacement) throw new Error(this.dataset.error);
    this.replaceWith(replacement);
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = replacement.dataset.itemCount;
    });
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { itemCount: Number(replacement.dataset.itemCount) }
    }));
    if (shouldOpen) replacement.open(trigger);
  }

  async change(line, quantity) {
    try {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message);
      await this.refresh();
    } catch (error) {
      const status = this.querySelector('[data-cart-status]');
      status.textContent = error.message || this.dataset.error;
      status.focus();
    }
  }
}

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-results]');
    this.input.addEventListener('input', debounce(() => this.search()));
    this.addEventListener('keydown', (event) => this.navigate(event));
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.close();
    });
  }

  async search() {
    const query = this.input.value.trim();
    if (query.length < 2) return this.close();
    this.input.setAttribute('aria-busy', 'true');
    try {
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,collection,page,article',
        'resources[limit]': '6',
        section_id: 'predictive-search'
      });
      const response = await fetch(`${root}search/suggest?${params}`);
      if (!response.ok) throw new Error();
      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      this.results.innerHTML = documentFragment.querySelector('#shopify-section-predictive-search')?.innerHTML || '';
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    } catch {
      this.close();
    } finally {
      this.input.removeAttribute('aria-busy');
    }
  }

  navigate(event) {
    const links = [...this.results.querySelectorAll('a')];
    if (!links.length || !['ArrowDown', 'ArrowUp', 'Escape'].includes(event.key)) return;
    event.preventDefault();
    if (event.key === 'Escape') {
      this.close();
      return this.input.focus();
    }
    const current = links.indexOf(document.activeElement);
    const next = event.key === 'ArrowDown' ? (current + 1) % links.length : (current - 1 + links.length) % links.length;
    links[next].focus();
  }

  close() {
    this.results.hidden = true;
    this.results.innerHTML = '';
    this.input.setAttribute('aria-expanded', 'false');
  }
}

class FacetFilters extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('change', debounce(() => this.form.requestSubmit(), 300));
  }
}

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url || !('IntersectionObserver' in window)) return this.load();
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      this.load();
    }, { rootMargin: '300px' });
    observer.observe(this);
  }

  async load() {
    const response = await fetch(this.dataset.url);
    if (!response.ok) return;
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const replacement = html.querySelector('product-recommendations');
    if (replacement?.innerHTML.trim()) this.innerHTML = replacement.innerHTML;
  }
}

customElements.define('quantity-input', QuantityInput);
customElements.define('variant-selects', VariantSelects);
customElements.define('product-form', ProductForm);
customElements.define('cart-drawer', CartDrawer);
customElements.define('predictive-search', PredictiveSearch);
customElements.define('facet-filters', FacetFilters);
customElements.define('product-recommendations', ProductRecommendations);

const closeAnimatedDialog = (dialog) => {
  if (!dialog?.open || dialog.classList.contains('is-closing')) return;
  dialog.classList.add('is-closing');
  document.body.classList.remove('drawer-open');
  window.setTimeout(() => {
    dialog.close();
    dialog.classList.remove('is-closing');
    dialog.returnFocus?.focus();
  }, motionDuration());
};

document.querySelectorAll('.nav-dropdown').forEach((dropdown) => {
  const summary = dropdown.querySelector(':scope > summary');
  let closeTimer;

  const close = (immediate = false) => {
    if (!dropdown.open) return;
    window.clearTimeout(closeTimer);
    summary.setAttribute('aria-expanded', 'false');
    if (immediate || prefersReducedMotion.matches) {
      dropdown.open = false;
      dropdown.classList.remove('is-closing');
      return;
    }
    dropdown.classList.add('is-closing');
    closeTimer = window.setTimeout(() => {
      dropdown.open = false;
      dropdown.classList.remove('is-closing');
    }, motionDuration());
  };

  summary.setAttribute('aria-expanded', dropdown.open ? 'true' : 'false');
  summary.addEventListener('click', (event) => {
    event.preventDefault();
    if (dropdown.open && !dropdown.classList.contains('is-closing')) {
      close();
      return;
    }
    document.querySelectorAll('.nav-dropdown[open]').forEach((other) => {
      if (other !== dropdown) other.dispatchEvent(new CustomEvent('nav:close', { detail: { immediate: true } }));
    });
    window.clearTimeout(closeTimer);
    dropdown.classList.remove('is-closing');
    dropdown.open = true;
    summary.setAttribute('aria-expanded', 'true');
  });
  dropdown.addEventListener('nav:close', (event) => close(Boolean(event.detail?.immediate)));
  dropdown.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    close();
    summary.focus();
  });
});

document.addEventListener('pointerdown', (event) => {
  document.querySelectorAll('.nav-dropdown[open]').forEach((dropdown) => {
    if (!dropdown.contains(event.target)) dropdown.dispatchEvent(new CustomEvent('nav:close'));
  });
});

const closeMobileNavigation = (navigation) => {
  if (!navigation?.open || navigation.classList.contains('is-closing')) return;
  navigation.classList.add('is-closing');
  window.setTimeout(() => {
    navigation.open = false;
    navigation.classList.remove('is-closing');
    document.querySelector(`[data-mobile-nav-toggle][aria-controls="${navigation.id}"]`)?.focus();
  }, motionDuration());
};

const syncMobileNavigationToggle = (navigation) => {
  const toggle = document.querySelector(`[data-mobile-nav-toggle][aria-controls="${navigation.id}"]`);
  toggle?.setAttribute('aria-expanded', String(navigation.open));
  document.body.classList.toggle('mobile-nav-open', navigation.open);
};

document.querySelectorAll('.mobile-nav').forEach((navigation) => {
  // Keep the drawer outside sticky/filtered ancestors so position:fixed covers the viewport.
  if (navigation.parentElement !== document.body) {
    document.body.appendChild(navigation);
  }
  navigation.addEventListener('toggle', () => {
    syncMobileNavigationToggle(navigation);
  });
  navigation.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    closeMobileNavigation(navigation);
  });
});

document.addEventListener('click', (event) => {
  const openTrigger = event.target.closest('[data-dialog-open]');
  const closeTrigger = event.target.closest('[data-dialog-close]');
  const mobileNavToggle = event.target.closest('[data-mobile-nav-toggle]');
  const mobileNavClose = event.target.closest('[data-mobile-nav-close]');
  const mobileNavigationBackdrop = event.target.matches('.mobile-nav') ? event.target : null;
  if (mobileNavToggle) {
    const navigation = document.getElementById(mobileNavToggle.getAttribute('aria-controls'));
    if (!navigation) return;
    if (navigation.open) {
      closeMobileNavigation(navigation);
    } else {
      navigation.classList.remove('is-closing');
      navigation.open = true;
      navigation.querySelector('.mobile-panel__close, a, button, summary')?.focus();
    }
  }
  if (mobileNavigationBackdrop) closeMobileNavigation(mobileNavigationBackdrop);
  if (mobileNavClose) {
    const navigation = mobileNavClose.closest('details');
    closeMobileNavigation(navigation);
  }
  if (openTrigger) {
    const dialog = document.getElementById(openTrigger.dataset.dialogOpen);
    if (dialog) {
      dialog.classList.remove('is-closing');
      if (!dialog.open) dialog.showModal();
      dialog.returnFocus = openTrigger;
      document.body.classList.add('drawer-open');
      const searchInput = dialog.querySelector('input[type="search"]');
      if (searchInput) {
        const pageInput = document.querySelector('.main-search input[type="search"]');
        if (pageInput?.value && !searchInput.value) searchInput.value = pageInput.value;
        window.requestAnimationFrame(() => {
          searchInput.focus({ preventScroll: true });
          if (searchInput.value) searchInput.select();
          searchInput.dispatchEvent(new Event('input', { bubbles: true }));
        });
      } else {
        dialog.querySelector('input, button, [href]')?.focus();
      }
      if (!dialog.dataset.cancelBound) {
        dialog.dataset.cancelBound = 'true';
        dialog.addEventListener('cancel', (cancelEvent) => {
          cancelEvent.preventDefault();
          closeAnimatedDialog(dialog);
        });
      }
    }
  }
  if (closeTrigger) {
    closeAnimatedDialog(closeTrigger.closest('dialog'));
  }
});

document.addEventListener('click', (event) => {
  const dialog = event.target.closest('dialog');
  if (dialog && event.target === dialog && !dialog.closest('cart-drawer')) {
    closeAnimatedDialog(dialog);
  }
});

document.addEventListener('change', (event) => {
  const details = event.target.closest?.('.facets details, .plant-filters details');
  if (details) facetKeepOpen = detailsKey(details);
  const sort = event.target.closest('select[name="sort_by"]');
  sort?.form?.requestSubmit();
});

document.addEventListener('cart:updated', (event) => {
  document.querySelectorAll('[data-cart-count]').forEach((badge) => {
    if (Number.isFinite(event.detail?.itemCount)) badge.textContent = event.detail.itemCount;
    badge.classList.remove('is-bumping');
    void badge.offsetWidth;
    badge.classList.add('is-bumping');
    window.setTimeout(() => badge.classList.remove('is-bumping'), 450);
  });
});

const siteHeader = document.querySelector('.site-header');
const updateHeader = () => siteHeader?.classList.toggle('is-scrolled', window.scrollY > 12);
updateHeader();
window.addEventListener('scroll', updateHeader, { passive: true });

const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const initHeroCollageTilt = () => {
  const collage = document.querySelector('.hero__collage');
  if (!collage) return;

  const resetTilt = (item) => {
    item.style.setProperty('--tilt-x', '0deg');
    item.style.setProperty('--tilt-y', '0deg');
  };

  collage.querySelectorAll('.hero__collage-item').forEach((item) => {
    item.addEventListener('pointermove', (event) => {
      if (!finePointer.matches || prefersReducedMotion.matches || event.pointerType !== 'mouse') {
        resetTilt(item);
        return;
      }
      const rect = item.getBoundingClientRect();
      const px = (event.clientX - rect.left) / rect.width - 0.5;
      const py = (event.clientY - rect.top) / rect.height - 0.5;
      item.style.setProperty('--tilt-x', `${(-py * 4).toFixed(2)}deg`);
      item.style.setProperty('--tilt-y', `${(px * 4).toFixed(2)}deg`);
    });
    item.addEventListener('pointerleave', () => resetTilt(item));
    item.addEventListener('blur', () => resetTilt(item));
  });
};
initHeroCollageTilt();

const revealSelector = '[data-reveal], .product-card, .section-heading, .article-card';
let revealObserver = null;

const prepareReveal = (element, index) => {
  element.dataset.reveal = '';
  element.style.setProperty('--reveal-index', String(index % 6));
};

const observeReveal = (root = document) => {
  const elements = [...root.querySelectorAll(revealSelector)];
  if (root !== document && root.matches?.(revealSelector)) elements.unshift(root);
  elements.forEach((element, index) => prepareReveal(element, index));

  if (!elements.length) return;
  if (!('IntersectionObserver' in window) || prefersReducedMotion.matches || window.Shopify?.designMode) {
    elements.forEach((element) => element.classList.add('is-visible'));
    return;
  }

  revealObserver ||= new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: .08 });

  elements.forEach((element) => revealObserver.observe(element));
};

observeReveal();
document.addEventListener('shopify:section:load', (event) => observeReveal(event.target));

const articleContent = document.querySelector('[data-article-content]');
const articleToc = document.querySelector('[data-article-toc]');
if (articleContent && articleToc) {
  const headings = [...articleContent.querySelectorAll('h2, h3')];
  if (headings.length >= 3) {
    const list = articleToc.querySelector('ol');
    headings.forEach((heading, index) => {
      heading.id ||= `article-heading-${index + 1}`;
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent;
      item.classList.toggle('is-nested', heading.tagName === 'H3');
      item.append(link);
      list.append(item);
    });
    articleToc.hidden = false;
    articleToc.open = window.matchMedia('(min-width: 64rem)').matches;
  }
}

const nearestStep = (steps, value) => steps.reduce((best, step) => (
  Math.abs(step - value) < Math.abs(best - value) ? step : best
), steps[0]);

const formatPlantRange = (min, max, unit) => {
  if (unit === '/5') return `${min}/5 – ${max}/5`;
  if (unit) return `${min}–${max} ${unit}`;
  return `${min}–${max}`;
};

class PlantRangeFilter extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.steps = String(this.dataset.steps || '').split(',').map(Number).filter((step) => Number.isFinite(step));
    if (!this.steps.length) return;

    this.key = this.dataset.key;
    this.unit = this.dataset.unit || '';
    this.minStep = this.steps[0];
    this.maxStep = this.steps[this.steps.length - 1];
    const hasMin = this.dataset.activeMin !== '';
    const hasMax = this.dataset.activeMax !== '';
    this.min = hasMin ? nearestStep(this.steps, Number(this.dataset.activeMin)) : this.minStep;
    this.max = hasMax ? nearestStep(this.steps, Number(this.dataset.activeMax)) : this.maxStep;

    this.minNumber = this.querySelector('input[type="number"][data-bound="min"]');
    this.maxNumber = this.querySelector('input[type="number"][data-bound="max"]');
    this.minRange = this.querySelector('input[type="range"][data-bound="min"]');
    this.maxRange = this.querySelector('input[type="range"][data-bound="max"]');
    this.display = this.querySelector('[data-display]');
    if (!this.minNumber || !this.maxNumber || !this.minRange || !this.maxRange) return;

    for (const input of [this.minNumber, this.maxNumber, this.minRange, this.maxRange]) {
      input.min = this.minStep;
      input.max = this.maxStep;
      input.step = 1;
    }

    this.syncInputs();
    this.minRange.addEventListener('input', () => this.fromControl(this.minRange, 'min', false));
    this.maxRange.addEventListener('input', () => this.fromControl(this.maxRange, 'max', false));
    this.minRange.addEventListener('change', () => this.apply());
    this.maxRange.addEventListener('change', () => this.apply());
    this.minNumber.addEventListener('change', () => this.fromControl(this.minNumber, 'min', true));
    this.maxNumber.addEventListener('change', () => this.fromControl(this.maxNumber, 'max', true));
  }

  fromControl(input, bound, apply) {
    let min = nearestStep(this.steps, Number(bound === 'min' ? input.value : this.min));
    let max = nearestStep(this.steps, Number(bound === 'max' ? input.value : this.max));
    if (bound === 'min' && min > max) min = max;
    if (bound === 'max' && max < min) max = min;
    this.min = min;
    this.max = max;
    this.syncInputs();
    if (apply) this.apply();
  }

  syncInputs() {
    this.minNumber.value = this.min;
    this.maxNumber.value = this.max;
    this.minRange.value = this.min;
    this.maxRange.value = this.max;
    if (this.display) this.display.textContent = formatPlantRange(this.min, this.max, this.unit);
    const root = this.closest('[data-plant-filters]');
    let state = { base: '', tags: [] };
    try {
      state = JSON.parse(root?.querySelector('[data-plant-filter-state]')?.textContent || '{}');
    } catch (error) {
      state = { base: '', tags: [] };
    }
    const catalog = plantCatalogCache.get(String(state.base || '').replace(/\/$/, ''));
    if (catalog) writeRangeCount(this, catalog, Array.isArray(state.tags) ? state.tags.map(String) : []);
  }

  apply() {
    const root = this.closest('[data-plant-filters]');
    const stateNode = root?.querySelector('[data-plant-filter-state]');
    let state = { base: '', tags: [] };
    try {
      state = JSON.parse(stateNode?.textContent || '{}');
    } catch (error) {
      state = { base: '', tags: [] };
    }

    const tags = (Array.isArray(state.tags) ? state.tags : []).filter((tag) => (
      !String(tag).startsWith(`wr-${this.key}-lte-`) && !String(tag).startsWith(`wr-${this.key}-gte-`)
    ));
    if (this.min !== this.minStep || this.max !== this.maxStep) {
      tags.push(`wr-${this.key}-gte-${this.min}`, `wr-${this.key}-lte-${this.max}`);
    }

    const base = String(state.base || '').replace(/\/$/, '');
    const encoded = tags.map((tag) => encodeURIComponent(tag).replace(/%3A/gi, ':'));
    const path = encoded.length ? `${base}/${encoded.join('+')}` : base;
    const nextUrl = `${path}${window.location.search}`;
    const keepOpen = this.closest('details')?.getAttribute('data-filter-group') || '';
    if (document.querySelector('[data-collection-live]')) renderCollection(nextUrl, { keepOpen: keepOpen || null });
    else window.location.assign(nextUrl);
  }
}

let collectionRequest = 0;
let collectionAbort = null;
let facetKeepOpen = '';

const detailsKey = (details) => details?.getAttribute('data-filter-group') || details?.querySelector('summary span:not(.filter-summary)')?.textContent.trim() || '';

async function renderCollection(url, { push = true, scroll = 'keep', focus = null, keepOpen = null } = {}) {
  const section = document.querySelector('[data-collection-live]')?.closest('.shopify-section');
  const sectionId = section?.querySelector('[data-collection-live]')?.dataset.sectionId;
  if (!section || !sectionId) {
    window.location.assign(url);
    return;
  }

  const requestId = ++collectionRequest;
  collectionAbort?.abort();
  const controller = new AbortController();
  collectionAbort = controller;

  const openGroups = new Set([...section.querySelectorAll('details[open]')].map(detailsKey).filter(Boolean));
  if (keepOpen) openGroups.add(keepOpen);
  const listScroll = new Map();
  section.querySelectorAll('details').forEach((details) => {
    const name = detailsKey(details);
    const list = details.querySelector('.facets__list');
    if (!name || !list) return;
    listScroll.set(`${details.closest('dialog') ? 'dialog' : 'page'}:${name}`, list.scrollTop);
  });
  const dialog = section.querySelector('dialog[open]');
  const dialogId = dialog?.id || '';
  const drawerScroll = dialog?.querySelector('.drawer__body')?.scrollTop || 0;
  const scrollY = window.scrollY;
  section.querySelector('.collection-layout')?.classList.add('is-filtering');
  section.querySelector('.collection-layout')?.setAttribute('aria-busy', 'true');

  const fetchUrl = new URL(url, window.location.origin);
  fetchUrl.searchParams.set('section_id', sectionId);

  try {
    const response = await fetch(fetchUrl, { signal: controller.signal });
    if (!response.ok) throw new Error('Collection filter request failed');
    const html = await response.text();
    if (requestId !== collectionRequest) return;
    const next = new DOMParser().parseFromString(html, 'text/html').getElementById(`shopify-section-${sectionId}`);
    if (!next) throw new Error('Collection section missing');

    section.querySelectorAll('[data-reveal], .product-card').forEach((element) => revealObserver?.unobserve(element));
    if (dialog?.open) dialog.close();
    section.innerHTML = next.innerHTML;
    const restoreGroups = () => {
      section.querySelectorAll('details').forEach((details) => {
        const name = detailsKey(details);
        if (name && openGroups.has(name)) details.open = true;
        const list = details.querySelector('.facets__list');
        const top = name ? listScroll.get(`${details.closest('dialog') ? 'dialog' : 'page'}:${name}`) : 0;
        if (list && top) list.scrollTop = top;
      });
    };
    restoreGroups();

    if (dialogId) {
      const nextDialog = document.getElementById(dialogId);
      if (nextDialog && !nextDialog.open) {
        nextDialog.showModal();
        document.body.classList.add('drawer-open');
        const drawer = nextDialog.querySelector('.drawer__body');
        if (drawer) drawer.scrollTop = drawerScroll;
        if (!nextDialog.dataset.cancelBound) {
          nextDialog.dataset.cancelBound = 'true';
          nextDialog.addEventListener('cancel', (cancelEvent) => {
            cancelEvent.preventDefault();
            closeAnimatedDialog(nextDialog);
          });
        }
      } else if (!nextDialog) {
        document.body.classList.remove('drawer-open');
      }
    }

    observeReveal(section);
    applyPlantFilterCounts(section);
    const restoreScroll = () => {
      if (scroll === 'results') {
        const grid = section.querySelector('.product-grid, .commerce-empty');
        const top = grid ? grid.getBoundingClientRect().top + window.scrollY - 16 : 0;
        window.scrollTo(0, Math.max(0, top));
        return;
      }
      window.scrollTo(0, scrollY);
    };
    restoreScroll();

    if (focus?.group) {
      const pane = focus.dialog ? section.querySelector('dialog') : section.querySelector('.facets-desktop');
      const link = [...(pane || section).querySelectorAll('details')].filter((details) => detailsKey(details) === focus.group).flatMap((details) => [...details.querySelectorAll('a')]).find((anchor) => plantFilterLabel(anchor) === focus.label);
      link?.focus({ preventScroll: true });
    }

    const clean = new URL(url, window.location.origin);
    clean.searchParams.delete('section_id');
    const nextUrl = `${clean.pathname}${clean.search}${clean.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (push && nextUrl !== currentUrl) history.pushState({ collectionLive: true }, '', nextUrl);
    window.requestAnimationFrame(() => {
      restoreGroups();
      restoreScroll();
      window.requestAnimationFrame(restoreScroll);
    });
  } catch (error) {
    if (error?.name === 'AbortError' || requestId !== collectionRequest) return;
    window.location.assign(url);
  }
}

document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = event.target.closest('a[href]');
  if (!link || link.target === '_blank' || !link.closest('.plant-filters, .active-facets, .facets, .pagination, .commerce-empty')) return;
  const section = link.closest('.shopify-section');
  if (!section?.querySelector('[data-collection-live]')) return;
  const next = new URL(link.href, window.location.origin);
  if (next.origin !== window.location.origin) return;
  if (link.getAttribute('aria-disabled') === 'true') {
    event.preventDefault();
    return;
  }
  event.preventDefault();
  const details = link.closest('details');
  const group = detailsKey(details);
  if (details && !event.target.closest('summary')) {
    const holdOpen = () => {
      if (!details.isConnected || details.open) return;
      window.setTimeout(() => {
        if (details.isConnected) details.open = true;
      }, 0);
    };
    details.addEventListener('toggle', holdOpen);
    window.setTimeout(() => details.removeEventListener('toggle', holdOpen), 800);
  }
  renderCollection(link.href, {
    scroll: link.closest('.pagination') ? 'results' : 'keep',
    focus: group ? { group, label: plantFilterLabel(link), dialog: Boolean(link.closest('dialog')) } : null,
    keepOpen: group || null
  });
});

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || (form.method || 'get').toLowerCase() !== 'get') return;
  if (!form.classList.contains('facets__form') && !form.querySelector('select[name="sort_by"]')) return;
  const section = form.closest('.shopify-section');
  if (!section?.querySelector('[data-collection-live]')) return;
  event.preventDefault();
  const action = new URL(form.action || window.location.href, window.location.origin);
  const next = new URL(action.pathname, window.location.origin);
  for (const [key, value] of new FormData(form).entries()) {
    if (String(value) !== '') next.searchParams.append(key, value);
  }
  const keepOpen = facetKeepOpen;
  facetKeepOpen = '';
  renderCollection(next, { keepOpen: keepOpen || null });
});

window.addEventListener('popstate', () => {
  if (!document.querySelector('[data-collection-live]')) return;
  renderCollection(window.location.href, { push: false });
});

const plantCatalogCache = new Map();
const plantCatalogLoads = new Map();

const plantFilterLabel = (link) => link?.querySelector('[data-plant-filter-label]')?.textContent.trim() || link?.textContent.trim() || '';

const catalogKey = (base) => String(base || '').replace(/\/$/, '');

async function plantCatalog(base) {
  const key = catalogKey(base);
  if (!key) return [];
  if (plantCatalogCache.has(key)) return plantCatalogCache.get(key);
  if (!plantCatalogLoads.has(key)) {
    plantCatalogLoads.set(key, (async () => {
      const products = [];
      for (let page = 1; page <= 20; page += 1) {
        const response = await fetch(`${key}/products.json?limit=250&page=${page}`);
        if (!response.ok) throw new Error('Plant catalog failed');
        const data = await response.json();
        const batch = Array.isArray(data.products) ? data.products : [];
        products.push(...batch.map((product) => Array.isArray(product.tags) ? product.tags.map(String) : []));
        if (batch.length < 250) break;
      }
      plantCatalogCache.set(key, products);
      return products;
    })().catch((error) => {
      plantCatalogLoads.delete(key);
      throw error;
    }));
  }
  return plantCatalogLoads.get(key);
}

const matchingPlantCount = (catalog, required) => catalog.reduce((sum, tags) => sum + (required.every((tag) => tags.includes(tag)) ? 1 : 0), 0);

function writeRangeCount(range, catalog, tags) {
  const key = range.dataset.key;
  const required = tags.filter((tag) => !tag.startsWith(`wr-${key}-gte-`) && !tag.startsWith(`wr-${key}-lte-`));
  const min = Number.isFinite(range.min) ? range.min : Number(range.dataset.activeMin);
  const max = Number.isFinite(range.max) ? range.max : Number(range.dataset.activeMax);
  const minStep = Number.isFinite(range.minStep) ? range.minStep : Number(String(range.dataset.steps || '').split(',')[0]);
  const maxStep = Number.isFinite(range.maxStep) ? range.maxStep : Number(String(range.dataset.steps || '').split(',').at(-1));
  if (Number.isFinite(min) && Number.isFinite(max) && (min !== minStep || max !== maxStep)) {
    required.push(`wr-${key}-gte-${min}`, `wr-${key}-lte-${max}`);
  }
  const countNode = range.querySelector('[data-range-count]');
  if (countNode) countNode.textContent = String(matchingPlantCount(catalog, required));
}

async function applyPlantFilterCounts(root = document) {
  const blocks = [...root.querySelectorAll('[data-plant-filters]')];
  await Promise.all(blocks.map(async (block) => {
    let state = { base: '', tags: [] };
    try {
      state = JSON.parse(block.querySelector('[data-plant-filter-state]')?.textContent || '{}');
    } catch (error) {
      return;
    }
    const tags = Array.isArray(state.tags) ? state.tags.map(String) : [];
    block.setAttribute('aria-busy', 'true');
    let catalog = [];
    try {
      catalog = await plantCatalog(state.base);
    } catch (error) {
      block.removeAttribute('aria-busy');
      return;
    }
    if (!block.isConnected) return;
    const filtering = tags.length > 0;
    block.querySelectorAll('a[data-tag]').forEach((link) => {
      const tag = link.dataset.tag || '';
      const splitAt = tag.indexOf(':');
      const prefix = splitAt >= 0 ? tag.slice(0, splitAt + 1) : tag;
      const required = tags.filter((current) => current !== tag && !current.startsWith(prefix));
      required.push(tag);
      const count = matchingPlantCount(catalog, required);
      const countNode = link.querySelector('[data-plant-filter-count]');
      if (countNode) countNode.textContent = String(count);
      const unavailable = filtering && count === 0 && !link.classList.contains('active-tag');
      link.classList.toggle('is-unavailable', unavailable);
      link.closest('li')?.classList.toggle('is-unavailable', unavailable);
      if (unavailable) link.setAttribute('aria-disabled', 'true');
      else link.removeAttribute('aria-disabled');
    });
    block.querySelectorAll('.plant-filters__group').forEach((group) => {
      const options = [...group.querySelectorAll('a[data-tag]')];
      const visible = options.some((link) => !link.classList.contains('is-unavailable'));
      group.classList.toggle('is-unavailable', filtering && options.length > 0 && !visible);
    });
    block.querySelectorAll('plant-range-filter').forEach((range) => writeRangeCount(range, catalog, tags));
    block.removeAttribute('aria-busy');
  }));
}

if (!customElements.get('plant-range-filter')) {
  customElements.define('plant-range-filter', PlantRangeFilter);
}
applyPlantFilterCounts();
})();
















